import apiClient from "../../services/apiClient";

// 레벨테스트 관리 세 탭(재원생·신규생·단어리스트)이 쓰는 자료를 한 번에 받는다.
// 탭마다 따로 부르면 같은 학원 조회가 세 번 나가서 느리다.
// nickname 은 운영진만 쓴다. 다른 학원의 관리 화면을 그대로 열어 본다.
export async function fetchTeacherLevelTestAPI(nickname = "") {
  const url = nickname
    ? `/api/v1/echo/teacher/leveltest?nickname=${encodeURIComponent(nickname)}`
    : "/api/v1/echo/teacher/leveltest";
  const response = await apiClient.get(url, { withAuth: true });
  return response.data;
}

// 응시코드에 '누구에게 줬는지' 메모를 저장한다.
export async function saveAccessCodeMemoAPI(code, memo) {
  const response = await apiClient.put(
    "/api/v1/echo/teacher/code-memo",
    { code, memo },
    { withAuth: true }
  );
  return response.data;
}

// 학생별 학습 내역에 얹을 레벨테스트 기록과 복습 내역.
export async function fetchStudentHistoryAPI() {
  const response = await apiClient.get("/api/v1/echo/teacher/student-history", {
    withAuth: true,
  });
  return response.data;
}

// 응시코드 한 장에 신규생을 등록한다. 등록해야 응시 안내 알림톡을 보낼 수 있다.
export async function saveLevelTestInviteAPI(invite) {
  const response = await apiClient.put(
    "/api/v1/echo/teacher/invite",
    invite,
    { withAuth: true }
  );
  return response.data;
}

// 등록해 둔 신규생에게 응시 안내 알림톡을 보낸다. 누르면 그 자리에서 실제로 나간다.
export async function sendLevelTestInviteAPI(code) {
  const response = await apiClient.post(
    "/api/v1/echo/teacher/invite/notify",
    { code },
    { withAuth: true }
  );
  return response.data;
}

// 재원생에게 이번 달 응시 안내 알림톡을 보낸다. 신규생과 템플릿이 다르다.
export async function sendEnrolledNoticeAPI(studentId) {
  const response = await apiClient.post(
    "/api/v1/echo/teacher/enrolled/notify",
    { student_id: studentId },
    { withAuth: true }
  );
  return response.data;
}

// 레벨테스트 관리 탭을 보여줄지. 화면 정리용이고 실제 차단은 서버가 한다.
export async function fetchLevelTestEntitlementAPI() {
  const response = await apiClient.get("/api/v1/echo/teacher/entitlement", {
    withAuth: true,
  });
  return response.data;
}

// 계약하지 않은 학원(라이트 등급)이 보는 코드함. 자기 코드와 리포트만 온다.
export async function fetchCodeVaultAPI() {
  const response = await apiClient.get("/api/v1/echo/teacher/code-vault", {
    withAuth: true,
  });
  return response.data;
}

// 리포트를 만든다. **응시가 끝나도 자동으로 굽지 않는다.** AI 를 한 아이에 두 번
// 부르므로 느리고, 실패하면 아이 화면에 오류가 뜬다. 대신 원장이 필요할 때 누른다
// (api/CLAUDE.md 의 '리포트는 자동으로 굽지 않는다').
//
// 굽는 데 1분 안팎이라 서버는 시작만 시키고 바로 답한다. 응답의 status 는
// none / building / done / failed 넷이고, 화면이 몇 초 간격으로 되물어 본다.
//
// 누르는 자리가 둘이라 함수도 둘이다. 코드함은 응시코드로(자격의 근거가 코드),
// 레벨테스트 관리는 응시로(자격의 근거가 학원) 부른다.
export async function buildLevelTestReportByCodeAPI(code) {
  const response = await apiClient.post(
    "/api/v1/echo/teacher/report/build",
    { code },
    { withAuth: true }
  );
  return response.data;
}

export async function fetchLevelTestReportStatusByCodeAPI(code) {
  const response = await apiClient.get(
    `/api/v1/echo/teacher/report/build?code=${encodeURIComponent(code)}`,
    { withAuth: true }
  );
  return response.data;
}

export async function buildLevelTestReportBySessionAPI(sessionId) {
  const response = await apiClient.post(
    "/api/v1/echo/teacher/report",
    { session_id: sessionId },
    { withAuth: true }
  );
  return response.data;
}

export async function fetchLevelTestReportStatusBySessionAPI(sessionId) {
  const response = await apiClient.get(
    `/api/v1/echo/teacher/report?session_id=${encodeURIComponent(sessionId)}`,
    { withAuth: true }
  );
  return response.data;
}

/* ── 운영진 전용 ──────────────────────────────────────────
 * 레테를 본 사람 전체 내역, 응시코드 발급, 리포트가 없는 응시.
 * 서버가 `is_admin` 으로 다시 한 번 막으므로 화면 정리용일 뿐이다. */

// 레테 본 사람 전체 내역. 학원별·온라인을 한 표에서 본다.
export async function fetchAdminSessionsAPI({ month, academy, q } = {}) {
  const params = new URLSearchParams();
  if (month) params.set("month", month);
  if (academy) params.set("academy", academy);
  if (q) params.set("q", q);
  const query = params.toString();
  const response = await apiClient.get(
    `/api/v1/echo/admin/sessions${query ? `?${query}` : ""}`,
    { withAuth: true }
  );
  return response.data;
}

// 코드를 줄 계정을 이름·아이디·학원명으로 찾는다.
export async function searchCodeReceiverAPI(keyword) {
  const response = await apiClient.get(
    `/api/v1/echo/admin/give-codes?q=${encodeURIComponent(keyword)}`,
    { withAuth: true }
  );
  return response.data;
}

// 그 계정에 응시코드를 넣는다. 관리 명령과 같은 함수를 서버가 쓴다.
export async function giveCodesAPI({ nickname, count, expires, memo }) {
  const response = await apiClient.post(
    "/api/v1/echo/admin/give-codes",
    { nickname, count, expires, memo },
    { withAuth: true }
  );
  return response.data;
}

// 리포트가 없는 응시. 주인을 못 찾은 것은 아무도 못 굽기 때문에 여기서 굽는다.
export async function fetchMissingReportsAPI() {
  const response = await apiClient.get("/api/v1/echo/admin/missing-reports", {
    withAuth: true,
  });
  return response.data;
}

export async function buildMissingReportAPI(sessionId) {
  const response = await apiClient.post(
    "/api/v1/echo/admin/missing-reports",
    sessionId ? { session_id: sessionId } : {},
    { withAuth: true }
  );
  return response.data;
}

// 자기 학원 후기 목록. 레테 관리의 '후기' 갈래가 쓴다.
export async function fetchAcademyReviewsAPI() {
  const response = await apiClient.get("/api/v1/echo/teacher/reviews", {
    withAuth: true,
  });
  return response.data;
}

// 후기를 지인에듀 홈페이지 성적향상사례에 올리거나 내린다.
export async function publishAcademyReviewAPI(reviewId, publish) {
  const response = await apiClient.put(
    "/api/v1/echo/teacher/reviews",
    { review_id: reviewId, publish },
    { withAuth: true }
  );
  return response.data;
}
